<script lang="ts">
  import { onDestroy } from 'svelte';
  import { markerCanvasAngle } from '../lib/wheelLayout';
  import type { WheelLayout, WheelSegment } from '../lib/wheelLayout';
  import type { RouletteSettings } from '../lib/types';

  interface Props {
    layout: WheelLayout;
    /** 現在のホイール回転角 (ラジアン) */
    rotation: number;
    settings: RouletteSettings;
    /** ホイール領域がタップ/クリックされたとき */
    ontap?: () => void;
    /** マーカーをドラッグして角度変更 (deg, 12時=0, CW正) */
    onmarkerdrag?: (deg: number) => void;
    /** ホイール外周リングを発光させる演出 variant (1〜5)。null/undefined で無効。
     *  非 null の間は内部 RAF で再描画される。 */
    glowVariant?: number | null;
  }

  let { layout, rotation, settings, ontap, onmarkerdrag, glowVariant = null }: Props = $props();
  let dragging = $state(false);

  let canvas = $state<HTMLCanvasElement | undefined>(undefined);
  let container = $state<HTMLDivElement | undefined>(undefined);
  let size = $state(0);
  let observer: ResizeObserver | undefined;
  // glow ring 用 RAF 状態
  let glowRafId: number | null = null;
  let glowStartTime = 0;
  const GLOW_DURATION_MS = 1800;

  // --- ResizeObserver ---
  $effect(() => {
    const el = container;
    if (!el) return;
    observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const s = Math.floor(Math.min(width, height));
        if (s !== size) size = s;
      }
    });
    observer.observe(el);
    return () => observer?.disconnect();
  });

  // --- 再描画トリガー ---
  $effect(() => {
    const c = canvas;
    const sz = size;
    const rot = rotation;
    const wl = layout;
    const s = settings;
    if (!c || sz <= 0) return;
    drawWheel(c, wl, rot, s, sz);
  });

  // --- glow 演出: variant が立つたびに RAF ループを起動して時間に応じた描画 ---
  $effect(() => {
    const v = glowVariant;
    if (!canvas || size <= 0) return;
    if (v && v >= 1 && v <= 5) {
      glowStartTime = performance.now();
      const tick = () => {
        if (!canvas) return;
        drawWheel(canvas, layout, rotation, settings, size);
        const elapsed = performance.now() - glowStartTime;
        if (elapsed < GLOW_DURATION_MS && glowVariant) {
          glowRafId = requestAnimationFrame(tick);
        } else {
          glowRafId = null;
          // glow 終了後も通常状態を再描画
          if (canvas) drawWheel(canvas, layout, rotation, settings, size);
        }
      };
      glowRafId = requestAnimationFrame(tick);
      return () => {
        if (glowRafId !== null) {
          cancelAnimationFrame(glowRafId);
          glowRafId = null;
        }
      };
    }
  });

  onDestroy(() => {
    observer?.disconnect();
    if (glowRafId !== null) cancelAnimationFrame(glowRafId);
  });

  // ─────────────────────────────────────────────────────────
  //  描画
  // ─────────────────────────────────────────────────────────

  function drawWheel(
    c: HTMLCanvasElement,
    wl: WheelLayout,
    rot: number,
    s: RouletteSettings,
    sz: number,
  ): void {
    const dpr = window.devicePixelRatio || 1;
    // canvas サイズが変わったときだけ属性を更新 (パフォーマンス)
    if (c.width !== sz * dpr || c.height !== sz * dpr) {
      c.width = sz * dpr;
      c.height = sz * dpr;
      c.style.width = sz + 'px';
      c.style.height = sz + 'px';
    }

    const ctx = c.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, sz, sz);

    const cx = sz / 2;
    const cy = sz / 2;
    const mSize = Math.max(8, sz * 0.035);
    // markerStyle に応じて R を調整 (外側マーカー時のみ縮小)
    const needsExt = s.markerStyle !== 'inside';
    const markerExt = needsExt ? mSize * 2 : 4;
    const R = sz / 2 - markerExt - 4;
    const innerR = R * 0.10;

    if (wl.segments.length === 0) {
      drawEmptyWheel(ctx, cx, cy, R, innerR, s, mSize);
      return;
    }

    // segment[0] は 12 時から開始 (Windows版と同じ)。
    // 並び方向は settings.segmentDirection に従う (cw=右回り、ccw=左回り)。
    const segDir = s.segmentDirection === 'ccw' ? -1 : 1;
    drawSegments(ctx, wl.segments, rot, cx, cy, R, segDir);
    drawTexts(ctx, wl.segments, rot, cx, cy, R, innerR, s, segDir);
    drawCenter(ctx, cx, cy, innerR);
    drawOuterRing(ctx, cx, cy, R);
    drawMarker(ctx, cx, cy, R, s, mSize);
    // ホイール外周リングのグロー演出 (glowVariant が立っている間)
    if (glowVariant && glowVariant >= 1 && glowVariant <= 5) {
      const elapsed = performance.now() - glowStartTime;
      const t = Math.max(0, Math.min(1, elapsed / GLOW_DURATION_MS));
      drawGlowRing(ctx, cx, cy, R, glowVariant, t);
    }
  }

  /** ホイール外周枠 (Canvas 内最外円、半径 R) を発光させる演出。
   *  ポインター (マーカー) は外側に別途描画されるため、本リングはホイール円のみを強調する。
   *  variant 1: 虹色循環 / 2: ゴールド単色脈動 / 3: 2回点滅 / 4: 6色回転 / 5: ピンク紫シマー */
  function drawGlowRing(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number, R: number,
    variant: number, t: number,
  ): void {
    // フェード: 0-15% で立ち上がり、85-100% でフェードアウト
    const fade = (t < 0.15) ? (t / 0.15)
              : (t > 0.85) ? ((1 - t) / 0.15)
              : 1;
    if (fade <= 0) return;

    ctx.save();
    ctx.lineCap = 'round';

    switch (variant) {
      case 1: { // 虹色循環: HSL の hue を時間で回す
        const hue = (t * 360 * 1.2) % 360;
        const color = `hsl(${hue}, 100%, 55%)`;
        ctx.strokeStyle = color;
        ctx.shadowColor = color;
        ctx.shadowBlur = (18 + Math.sin(t * Math.PI * 2) * 8) * fade;
        ctx.lineWidth = 5 + Math.sin(t * Math.PI) * 3;
        ctx.globalAlpha = 0.95 * fade;
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case 2: { // ゴールド単色脈動
        const intensity = 0.7 + 0.3 * Math.sin(t * Math.PI * 2);
        ctx.strokeStyle = `hsl(45, 100%, 55%)`;
        ctx.shadowColor = '#ffaa00';
        ctx.shadowBlur = (20 + intensity * 14) * fade;
        ctx.lineWidth = 6 + intensity * 2;
        ctx.globalAlpha = (0.6 + intensity * 0.4) * fade;
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case 3: { // 2 回点滅 (赤系)
        const blink1 = Math.max(0, 1 - Math.abs(t - 0.20) / 0.13);
        const blink2 = Math.max(0, 1 - Math.abs(t - 0.55) / 0.13);
        const intensity = Math.max(blink1, blink2);
        if (intensity > 0.05) {
          ctx.strokeStyle = `rgba(255, 80, 80, ${intensity})`;
          ctx.shadowColor = '#ff5050';
          ctx.shadowBlur = 28 * intensity;
          ctx.lineWidth = 6;
          ctx.globalAlpha = fade;
          ctx.beginPath();
          ctx.arc(cx, cy, R, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;
      }
      case 4: { // 6 セグメント回転 (虹色)
        const rot = t * Math.PI * 2 * 1.5;
        const colors = ['#ff5050', '#ffaa28', '#fff03c', '#50dc50', '#3ca0ff', '#b450ff'];
        ctx.lineWidth = 5;
        ctx.shadowBlur = 14 * fade;
        ctx.globalAlpha = fade;
        for (let i = 0; i < 6; i++) {
          const startAngle = rot + (i / 6) * Math.PI * 2;
          const endAngle = startAngle + Math.PI * 2 / 6 + 0.02;
          ctx.strokeStyle = colors[i];
          ctx.shadowColor = colors[i];
          ctx.beginPath();
          ctx.arc(cx, cy, R, startAngle, endAngle);
          ctx.stroke();
        }
        break;
      }
      case 5: { // ピンク紫シマー
        const hue = 300 + 30 * Math.sin(t * Math.PI * 8);
        const intensity = 0.7 + 0.3 * Math.sin(t * Math.PI * 5);
        ctx.strokeStyle = `hsl(${hue}, 100%, 65%)`;
        ctx.shadowColor = '#ff80c0';
        ctx.shadowBlur = (20 + intensity * 16) * fade;
        ctx.lineWidth = 5 + intensity * 2;
        ctx.globalAlpha = intensity * fade;
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
    }

    ctx.restore();
  }

  function drawEmptyWheel(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number, R: number, innerR: number,
    s: RouletteSettings, mSize: number,
  ): void {
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.fillStyle = 'var(--color-surface, #2a2a2a)';
    ctx.fill();
    drawCenter(ctx, cx, cy, innerR);
    drawOuterRing(ctx, cx, cy, R);
    drawMarker(ctx, cx, cy, R, s, mSize);
  }

  function drawSegments(
    ctx: CanvasRenderingContext2D,
    segs: WheelSegment[],
    rot: number,
    cx: number, cy: number, R: number,
    segDir: number,
  ): void {
    // segDir=1: layout angle が増えると視覚的 CW (12 時から右回り)
    // segDir=-1: 反転、視覚的 CCW (12 時から左回り)
    const ccw = segDir === -1;
    for (const seg of segs) {
      const cStart = rot + segDir * seg.startAngle - Math.PI / 2;
      const cEnd   = rot + segDir * seg.endAngle   - Math.PI / 2;

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      // CCW のときは ctx.arc の anticlockwise=true で進行方向を反転
      ctx.arc(cx, cy, R, cStart, cEnd, ccw);
      ctx.closePath();
      ctx.fillStyle = seg.color;
      ctx.fill();

      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  const FONT = `-apple-system, BlinkMacSystemFont, 'Hiragino Sans', 'Yu Gothic UI', sans-serif`;

  function drawTexts(
    ctx: CanvasRenderingContext2D,
    segs: WheelSegment[],
    rot: number,
    cx: number, cy: number, R: number, innerR: number,
    s: RouletteSettings,
    segDir: number,
  ): void {
    for (const seg of segs) {
      const cMid = rot + segDir * seg.midAngle - Math.PI / 2;
      const sweep = seg.endAngle - seg.startAngle;
      drawSegmentText(ctx, seg.text, cMid, sweep, cx, cy, R, innerR, s);
    }
  }

  /** 二分探索: radial 配置でセグメント (扇形) 内に収まる最大フォントサイズ。
   *  扇形の特性: 半径が小さい位置ほど接線方向 (chord) が狭くなるため、
   *  テキストの **内側端 (= 中心に近い側)** での chord で fontSize 高さをチェックする。
   *
   *  outerR    = テキストが置かれる外端の半径 (outer 寄せなら safeOuterR)
   *  innerR    = テキストが侵入できる最も内側の半径 (= safeInnerR)
   *  halfSweep = 角度余白を引いた sweep の半分 (radians)
   *  alignOuter = true なら outer 寄せ (テキスト右端 = outerR)、false なら中央寄せ
   */
  function findMaxFontFitRadial(
    ctx: CanvasRenderingContext2D,
    text: string,
    outerR: number,
    innerR: number,
    halfSweep: number,
    alignOuter: boolean,
    maxFontSize: number,
    minFontSize: number = 7,
  ): number {
    if (text.length === 0) return minFontSize;
    const radialMax = outerR - innerR;
    if (radialMax <= 0 || halfSweep <= 0) return minFontSize;
    const sinHalf = Math.sin(halfSweep);

    let lo = minFontSize;
    let hi = Math.max(minFontSize, Math.floor(maxFontSize));
    let best = minFontSize;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      ctx.font = `600 ${mid}px ${FONT}`;
      const width = ctx.measureText(text).width;
      // 制約 1: テキスト幅 (= radial 方向) が radialMax 以下
      if (width > radialMax) {
        hi = mid - 1;
        continue;
      }
      // 制約 2: テキストの内側端での chord が fontSize × 1.15 (line-height 込み) 以上
      let innerEdge: number;
      if (alignOuter) {
        innerEdge = outerR - width;
      } else {
        const center = (outerR + innerR) * 0.5;
        innerEdge = center - width * 0.5;
      }
      const safeEdge = Math.max(innerR, innerEdge);
      const chordAtInner = 2 * safeEdge * sinHalf;
      // fontSize × 1.15 ≒ 実際の text height (font line-height)
      if (mid * 1.15 <= chordAtInner) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  }

  /** 二分探索: tangent / horizontal 用。テキスト幅 = chord、高さ = radial 方向。 */
  function findMaxFontFitTangent(
    ctx: CanvasRenderingContext2D,
    text: string,
    chordAtTextR: number,
    radialMax: number,
    maxFontSize: number,
    minFontSize: number = 7,
  ): number {
    if (text.length === 0 || chordAtTextR <= 0 || radialMax <= 0) return minFontSize;
    let lo = minFontSize;
    let hi = Math.max(minFontSize, Math.floor(maxFontSize));
    let best = minFontSize;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      ctx.font = `600 ${mid}px ${FONT}`;
      const width = ctx.measureText(text).width;
      // 幅 (chord) と高さ (line-height 込み) 両方の制約
      if (width <= chordAtTextR && mid * 1.15 <= radialMax) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  }

  function drawSegmentText(
    ctx: CanvasRenderingContext2D,
    text: string,
    canvasMid: number,
    sweep: number,
    cx: number, cy: number, R: number, innerR: number,
    s: RouletteSettings,
  ): void {
    const orientation = s.textOrientation;

    // ─── Windows 版同等の余白 (geometry.py / build_all_sector_layouts) ───
    // 半径余白: 内 max(12, R*0.12) / 外 max(8, R*0.05)
    const radialMarginInner = Math.max(12, R * 0.12);
    const radialMarginOuter = Math.max(8, R * 0.05);
    // 角度余白: max(0.5°, seg_arc*0.08) を 0.5° 上限と扇の 40% でクランプ
    const sweepDeg = (sweep * 180) / Math.PI;
    const angleMarginDeg = Math.min(
      Math.max(0.5, sweepDeg * 0.08),
      sweepDeg * 0.4,
    );
    const safeSweep = Math.max(
      0,
      sweep - 2 * ((angleMarginDeg * Math.PI) / 180),
    );
    const safeInnerR = innerR + radialMarginInner;
    const safeOuterR = R - radialMarginOuter;
    const safeRadialHeight = Math.max(0, safeOuterR - safeInnerR);
    if (safeRadialHeight <= 0 || safeSweep <= 0) return;

    // テキスト寄せ判定 (radial のみ 'outer' 効果)
    const useOuter = orientation === 'radial' && s.textAlign === 'outer';

    // 接線方向の弦長 (Windows版 tangential_chord_at): 2 * r * sin(θ/2)
    // テキスト基準半径 (outer なら外周寄り、center なら半径方向の中点)
    // 中点に置くことで findMaxFontFitRadial の center 制約計算と一致させる
    const textR = useOuter
      ? safeOuterR
      : (safeInnerR + safeOuterR) * 0.5;
    const halfSweep = safeSweep / 2;
    const tangentialChord = 2 * textR * Math.sin(halfSweep);

    // 各方向の利用可能寸法
    //   maxLong  = テキストが流れる方向の最大長
    //   maxShort = テキスト高さ方向の最大値
    let maxLong: number;
    let maxShort: number;
    if (orientation === 'radial') {
      // テキストは半径方向に流れる → 長さは radialHeight、高さは弦長
      maxLong = useOuter
        ? safeOuterR - safeInnerR
        : safeRadialHeight;
      maxShort = tangentialChord;
    } else if (orientation === 'tangent') {
      maxLong = tangentialChord;
      maxShort = safeRadialHeight;
    } else {
      // horizontal: 短い方を採用 (確実に収まる)
      maxLong = Math.min(tangentialChord, safeRadialHeight);
      maxShort = Math.min(tangentialChord, safeRadialHeight);
    }
    if (maxLong <= 0 || maxShort <= 0) return;

    // テキスト中心位置
    const tx = cx + textR * Math.cos(canvasMid);
    const ty = cy + textR * Math.sin(canvasMid);

    // ホイール全体の上限 (極端に巨大な文字回避)
    const wheelCap = Math.max(7, R * 0.32);
    let fontSize: number;
    if (s.textSizeMode === 'fixed') {
      fontSize = Math.max(7, Math.min(32, s.textFixedSize));
      // fixed でも overflow しないよう仮定の上限と比較
      if (orientation === 'radial') {
        const ceiling = findMaxFontFitRadial(
          ctx, text, safeOuterR, safeInnerR, halfSweep, useOuter, fontSize, 7,
        );
        if (fontSize > ceiling) fontSize = ceiling;
      } else {
        const ceiling = findMaxFontFitTangent(ctx, text, maxLong, maxShort, fontSize, 7);
        if (fontSize > ceiling) fontSize = ceiling;
      }
    } else {
      // 二分探索で「扇形内に収まる最大フォント」を求める
      if (orientation === 'radial') {
        fontSize = findMaxFontFitRadial(
          ctx, text, safeOuterR, safeInnerR, halfSweep, useOuter, wheelCap, 7,
        );
      } else {
        fontSize = findMaxFontFitTangent(ctx, text, maxLong, maxShort, wheelCap, 7);
      }
    }

    ctx.font = `600 ${fontSize}px ${FONT}`;

    // 二分探索で見つけたフォントでも収まらない場合は「…」で省略
    let displayText = text;
    if (ctx.measureText(displayText).width > maxLong) {
      const ellipsis = '…';
      while (
        displayText.length > 0 &&
        ctx.measureText(displayText + ellipsis).width > maxLong
      ) {
        displayText = displayText.slice(0, -1);
      }
      if (displayText.length === 0) {
        if (ctx.measureText(ellipsis).width <= maxLong) {
          displayText = ellipsis;
        } else {
          return; // 極小セグメント: 描画しない
        }
      } else {
        displayText += ellipsis;
      }
    }

    ctx.save();
    ctx.translate(tx, ty);
    if (orientation === 'radial') {
      ctx.rotate(canvasMid);
    } else if (orientation === 'tangent') {
      ctx.rotate(canvasMid + Math.PI / 2);
    }
    ctx.textAlign = useOuter ? 'right' : 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(0,0,0,0.75)';
    ctx.shadowBlur = 2;
    // fillText に maxWidth を渡してダブルセーフティ
    ctx.fillText(displayText, 0, 0, maxLong);
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  function drawCenter(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number, innerR: number,
  ): void {
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.fillStyle = '#111';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.45)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // 中心ドット
    ctx.beginPath();
    ctx.arc(cx, cy, innerR * 0.22, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
  }

  function drawOuterRing(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number, R: number,
  ): void {
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.18)';
    ctx.lineWidth = 3;
    ctx.stroke();
  }

  function drawMarker(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number, R: number,
    s: RouletteSettings, mSize: number,
  ): void {
    const mAngle = markerCanvasAngle(s.markerPosition, s.markerAngleDeg);
    // overlap: tip がホイール内側に少し入る (Windows版風)
    // outside: tip がホイール外周にちょうど触れる
    // inside : マーカー全体がホイール内側に収まる
    let tipDist: number;
    let baseDist: number;
    switch (s.markerStyle) {
      case 'inside':
        tipDist = R - mSize * 2.4;
        baseDist = R - 1;
        break;
      case 'outside':
        tipDist = R + 1;
        baseDist = R + mSize * 2;
        break;
      case 'overlap':
      default:
        tipDist = R - mSize * 0.7;
        baseDist = R + mSize * 2;
        break;
    }

    const tipX = cx + tipDist * Math.cos(mAngle);
    const tipY = cy + tipDist * Math.sin(mAngle);
    const px = Math.cos(mAngle + Math.PI / 2);
    const py = Math.sin(mAngle + Math.PI / 2);
    const b1x = cx + baseDist * Math.cos(mAngle) + px * mSize;
    const b1y = cy + baseDist * Math.sin(mAngle) + py * mSize;
    const b2x = cx + baseDist * Math.cos(mAngle) - px * mSize;
    const b2y = cy + baseDist * Math.sin(mAngle) - py * mSize;

    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(b1x, b1y);
    ctx.lineTo(b2x, b2y);
    ctx.closePath();
    if (s.markerStyle === 'inside') {
      // 内側配置時はテキストを隠さないよう半透明
      ctx.globalAlpha = 0.85;
    }
    ctx.shadowColor = 'rgba(0,0,0,0.45)';
    ctx.shadowBlur = 4;
    ctx.fillStyle = '#e94560';
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.globalAlpha = 1.0;
  }

  // ─────────────────────────────────────────────────────────
  //  タップ / ポインタイベント
  // ─────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────
  //  ヒット検出 / マーカードラッグ
  // ─────────────────────────────────────────────────────────

  /** Canvas 内ローカル座標から「12時=0, CW正, deg」のマーカー角を返す */
  function pointerToMarkerDeg(localX: number, localY: number): number {
    const sz = size;
    const cx = sz / 2;
    const cy = sz / 2;
    const dx = localX - cx;
    const dy = localY - cy;
    // 12時基準 CW: atan2(dx, -dy)
    const rad = Math.atan2(dx, -dy);
    const deg = (rad * 180) / Math.PI;
    return ((deg % 360) + 360) % 360;
  }

  /** ポインタがマーカー上にあるか */
  function isMarkerHit(localX: number, localY: number): boolean {
    const sz = size;
    if (sz <= 0) return false;
    const cx = sz / 2;
    const cy = sz / 2;
    const mSize = Math.max(8, sz * 0.035);
    const needsExt = settings.markerStyle !== 'inside';
    const markerExt = needsExt ? mSize * 2 : 4;
    const R = sz / 2 - markerExt - 4;

    // マーカー三角の中心位置 (canvas)
    const mAngle = markerCanvasAngle(settings.markerPosition, settings.markerAngleDeg);
    let mcDist: number;
    switch (settings.markerStyle) {
      case 'inside':
        mcDist = R - mSize * 1.7;
        break;
      case 'outside':
        mcDist = R + mSize;
        break;
      default:
        mcDist = R + mSize * 0.7;
        break;
    }
    const mcx = cx + mcDist * Math.cos(mAngle);
    const mcy = cy + mcDist * Math.sin(mAngle);
    const dx = localX - mcx;
    const dy = localY - mcy;
    return Math.sqrt(dx * dx + dy * dy) <= mSize * 1.6;
  }

  function getLocal(e: PointerEvent): { x: number; y: number } {
    const c = canvas;
    if (!c) return { x: 0, y: 0 };
    const rect = c.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function handlePointerDown(e: PointerEvent): void {
    e.preventDefault();
    const { x, y } = getLocal(e);
    if (onmarkerdrag && isMarkerHit(x, y)) {
      dragging = true;
      canvas?.setPointerCapture(e.pointerId);
      onmarkerdrag(pointerToMarkerDeg(x, y));
      return;
    }
    ontap?.();
  }

  function handlePointerMove(e: PointerEvent): void {
    if (!dragging || !onmarkerdrag) return;
    e.preventDefault();
    const { x, y } = getLocal(e);
    onmarkerdrag(pointerToMarkerDeg(x, y));
  }

  function handlePointerUp(e: PointerEvent): void {
    if (!dragging) return;
    dragging = false;
    canvas?.releasePointerCapture(e.pointerId);
  }
</script>

<div class="wheel-wrap" bind:this={container}>
  {#if size > 0}
    <canvas
      bind:this={canvas}
      style:width="{size}px"
      style:height="{size}px"
      style="touch-action: none; display: block;"
      onpointerdown={handlePointerDown}
      onpointermove={handlePointerMove}
      onpointerup={handlePointerUp}
      onpointercancel={handlePointerUp}
    ></canvas>
  {/if}
</div>

<style>
  .wheel-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
</style>
